/**
 * Story Studio API: list Grade 2 stories and stream video via learning-service.
 */
import { serviceUrls } from '@/config/serviceUrls';
import { getAuthHeaders } from '@/utils/apiHeaders';

const BASE = serviceUrls.learning.endsWith('/')
  ? serviceUrls.learning.slice(0, -1)
  : serviceUrls.learning;

export interface StoryVideo {
  blobName?: string;
  fileName?: string;
  mimetype?: string;
  url?: string;
}

export interface Story {
  _id: string;
  title: string;
  titleTamil?: string;
  grade?: number;
  lesson_no?: number;
  book_id?: string;
  /** Matches tutor sublesson id (e.g. `1.2`) when the video is for one embedded sublesson. */
  sublesson_no?: string | null;
  /** `rhyme` | `story` — helps auto-select when multiple videos exist for the same lesson. */
  segment_kind?: string | null;
  scenes?: unknown[];
  video?: StoryVideo;
}

/** Same shape as Story Studio URL query params used to pick a list row. */
export type StoryStudioResolveOpts = {
  sidFromUrl: string | null;
  lessonFromUrl: string | null;
  sublessonFromUrl: string;
  segmentFromUrl: string;
};

/**
 * Pick which `stories` list row matches tutor deep-links (book + lesson + sublesson + rhyme|story).
 * Must stay in sync with Story Studio page selection.
 */
export function resolveStoryStudioSelectedIndex(list: Story[], opts: StoryStudioResolveOpts): number {
  if (list.length === 0) return 0;
  const { sidFromUrl, lessonFromUrl, sublessonFromUrl, segmentFromUrl } = opts;
  if (sidFromUrl) {
    const i = list.findIndex((s) => s._id === sidFromUrl);
    if (i >= 0) return i;
  }
  const lessonN = lessonFromUrl ? parseInt(lessonFromUrl, 10) : NaN;
  const pool =
    !Number.isNaN(lessonN) && lessonN >= 1
      ? list.filter((s) => s.lesson_no === lessonN)
      : list;
  const useList = pool.length > 0 ? pool : list;

  const sub = sublessonFromUrl.trim();
  const lessonOnlySub = !sub || sub === '__lesson__';
  const rawSeg = segmentFromUrl.trim().toLowerCase();
  const wantSeg = rawSeg === 'rhyme' || rawSeg === 'story' ? rawSeg : '';

  if (!lessonOnlySub) {
    const subMatches = useList.filter((s) => String(s.sublesson_no ?? '').trim() === sub);
    if (subMatches.length === 1) {
      return list.indexOf(subMatches[0]);
    }
    if (subMatches.length > 1) {
      if (wantSeg) {
        const segM = subMatches.find(
          (s) => String(s.segment_kind ?? '').trim().toLowerCase() === wantSeg
        );
        if (segM) return list.indexOf(segM);
      }
      return list.indexOf(subMatches[0]);
    }
  }

  const levelMatches = useList.filter((s) => !String(s.sublesson_no ?? '').trim());
  if (levelMatches.length === 1) {
    return list.indexOf(levelMatches[0]);
  }
  if (levelMatches.length > 1) {
    if (wantSeg) {
      const segM = levelMatches.find(
        (s) => String(s.segment_kind ?? '').trim().toLowerCase() === wantSeg
      );
      if (segM) return list.indexOf(segM);
    }
    return list.indexOf(levelMatches[0]);
  }

  if (wantSeg) {
    const segM = useList.find(
      (s) => String(s.segment_kind ?? '').trim().toLowerCase() === wantSeg
    );
    if (segM) return list.indexOf(segM);
  }

  if (!Number.isNaN(lessonN) && lessonN >= 1) {
    const byLessonNo = list.findIndex((s) => s.lesson_no === lessonN);
    if (byLessonNo >= 0) return byLessonNo;
    return Math.min(lessonN - 1, list.length - 1);
  }
  return 0;
}

export function storyRowHasPlayableVideo(s: Story | undefined | null): boolean {
  if (!s?.video) return false;
  const v = s.video;
  return Boolean(
    (v.blobName && String(v.blobName).trim()) || (v.url && String(v.url).trim())
  );
}

/** Carries response body text for Story Studio error handling (e.g. 403 feature-disabled). */
export class LearningHttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly bodyText: string
  ) {
    super(message);
    this.name = 'LearningHttpError';
  }
}

export type FetchStoriesOptions = {
  bookId?: string;
  lessonNo?: number;
};

/** List stories for a grade (e.g. 2). Optional `bookId` / `lessonNo` scope Story Studio to the tutor book + lesson. */
export async function fetchStories(grade?: number, opts?: FetchStoriesOptions): Promise<Story[]> {
  const params = new URLSearchParams();
  if (typeof grade === 'number') params.set('grade', String(grade));
  if (opts?.bookId && opts.bookId.trim() !== '') params.set('book_id', opts.bookId.trim());
  if (typeof opts?.lessonNo === 'number' && opts.lessonNo >= 1) params.set('lesson_no', String(opts.lessonNo));
  const qs = params.toString();
  const r = await fetch(`${BASE}/v1/learning/stories${qs ? `?${qs}` : ''}`, {
    headers: getAuthHeaders(),
  });
  const bodyText = await r.text();
  if (!r.ok) {
    throw new LearningHttpError(`Stories API error: ${r.status} ${r.statusText}`, r.status, bodyText);
  }
  const data = JSON.parse(bodyText) as { stories?: Story[] };
  return data?.stories ?? [];
}

/** URL for the story video stream (GET /v1/learning/stories/{id}/video). */
export function storyVideoUrl(s: Story): string {
  if (!s?._id) return '';
  return `${BASE}/v1/learning/stories/${s._id}/video`;
}

/** Response from GET /v1/learning/stories/{id}/video (signed URL). */
export interface StoryVideoSignedUrlResponse {
  url: string;
  expiresIn: number;
}

/**
 * Fetch story video signed URL. The API returns JSON { url, expiresIn };
 * use the returned url as the video src for playback.
 */
export async function fetchStoryVideoSignedUrl(storyId: string): Promise<string> {
  const url = `${BASE}/v1/learning/stories/${storyId}/video`;
  const r = await fetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });
  const bodyText = await r.text();
  if (!r.ok) {
    throw new LearningHttpError(`Video failed: ${r.status} ${r.statusText}`, r.status, bodyText);
  }
  const data = JSON.parse(bodyText) as StoryVideoSignedUrlResponse;
  if (!data?.url) throw new Error('Video API did not return a URL');
  return data.url;
}

/** Create/update a `stories` row and upload video keyed by grade + lesson (+ optional book). No chunk `_id` needed. */
export interface ByLessonStoryUploadResponse {
  ok: boolean;
  _id: string;
  blobName: string;
  url: string;
  message?: string;
}

export async function uploadStoryVideoByLessonSlot(params: {
  file: File;
  grade: number;
  lesson_no: number;
  title: string;
  title_tamil?: string;
  book_id?: string;
  sublesson_no?: string;
  segment_kind?: 'rhyme' | 'story' | string;
}): Promise<ByLessonStoryUploadResponse> {
  const form = new FormData();
  form.append('file', params.file);
  form.append('grade', String(params.grade));
  form.append('lesson_no', String(params.lesson_no));
  form.append('title', params.title);
  if (params.title_tamil) form.append('title_tamil', params.title_tamil);
  if (params.book_id) form.append('book_id', params.book_id);
  if (params.sublesson_no && params.sublesson_no.trim() !== '')
    form.append('sublesson_no', params.sublesson_no.trim());
  if (params.segment_kind && params.segment_kind.trim() !== '')
    form.append('segment_kind', params.segment_kind.trim());
  const r = await fetch(`${BASE}/v1/learning/stories/by-lesson/video`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: form,
  });
  const bodyText = await r.text();
  if (!r.ok) {
    throw new LearningHttpError(`Upload failed: ${r.status} ${r.statusText}`, r.status, bodyText);
  }
  return JSON.parse(bodyText) as ByLessonStoryUploadResponse;
}
